import { dealThemePalette, defaultPalette, feelThemePalette } from '@guryou/colors';

import metropolisFont from './Metropolis/metropolis.regular.otf';
import { accordionOverrides } from './overrides/accordion';
import { autocomplete } from './overrides/autocomplete';
import buttonOverrides from './overrides/button';
import { cardOverrides } from './overrides/card';
import { containerOverrides } from './overrides/container';
import { expansionPanelOverrides } from './overrides/expansionPanel';
import { formOverrides } from './overrides/form';
import globalRules from './overrides/global';
import { loadingAnimations } from './overrides/loadingAnimations';
import modalOverrides from './overrides/modal';
import { navigationOverrides } from './overrides/navigation';
import { switchOverrides } from './overrides/switch';
import { tableOverrides } from './overrides/table';
import typographyOverrides from './overrides/typography';
import {
  borderDefinition,
  boxShadow,
  boxShadowLight,
  DEFAULT_SCALE,
  largeBorderRadius,
  mediumBorderRadius,
  sizes,
  smallBorderRadius,
  spacer,
} from './constants';

const bodyOverflowHidden = { body: { overflow: 'hidden' } };
const bodyOverflowAuto = { body: { overflow: 'auto', overflowX: 'hidden' } };

const createTheme = (palette, bodyOverflow, formControlSize, appBarMargin) => ({
  palette,
  typography: {
    fontSize: 12,
    fontFamily: ['Metropolis', 'sans-serif'].join(','),
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': {
          fontFamily: 'Metropolis',
          src: `url(${metropolisFont})`,
        },
        ...globalRules,
        ...loadingAnimations,
        ...autocomplete,
        ...bodyOverflow,
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${palette.secondary?.main || palette.primary.main} transparent`,
        },
        '*::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: palette.secondary?.main || palette.primary.main,
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '*::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
      },
    },

    MuiPopover: {
      paper: {
        '&.guryou-select-menu': {
          marginTop: 6,
          maxHeight: 320,
          border: '1px solid rgba(37, 37, 37, 0.1)',
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 12px 32px rgba(37, 37, 37, 0.14)',
          overflowY: 'auto',
        },
      },
    },
    MuiMenuItem: {
      root: {
        '.guryou-select-menu-list &': {
          minHeight: 40,
          margin: '3px 6px',
          padding: '8px 12px',
          borderRadius: 7,
          fontSize: 13,
          lineHeight: 1.35,
          transition: 'background-color 120ms ease, color 120ms ease',
          '&:hover': {
            backgroundColor: 'rgba(230, 97, 106, 0.09)',
          },
          '&.Mui-selected': {
            color: '#D94D61',
            fontWeight: 600,
            backgroundColor: 'rgba(230, 97, 106, 0.13)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(230, 97, 106, 0.18)',
          },
        },
      },
    },

    MuiFlatPagination: {
      root: {
        textAlign: 'center',
      },
    },
    MuiPickersBasePicker: {
      container: {
        width: 340,
        '& .MuiButton-root': {
          margin: 0,
          padding: 0,
        },
        '& .MuiTypography-h3': {
          fontSize: '3rem',
        },
        '& .MuiTypography-h4': {
          fontSize: '2.125rem',
          fontWeight: 400,
          lineHeight: 1.17,
          letterSpacing: '0.00735em',
        },
        '& .MuiTypography-subtitle1': {
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: 1.75,
          letterSpacing: '0.00938em',
        },
      },
    },
    MuiStepper: {
      root: {
        backgroundColor: 'transparent',
        borderBottom: borderDefinition(),
        paddingBottom: '12.5px',
        marginBottom: spacer.spacer3,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiListItem: {
      button: {
        '.customLi&:hover': {
          backgroundColor: 'white',
          '& div span': {
            color: '#252525',
          },
          '& .expandIcon path': {
            color: '#252525',
          },
        },
      },
      root: {
        '&.w-auto': {
          width: 'auto',
        },
      },
    },
    MuiSvgIcon: {
      root: {
        '&.colorWhite': {
          fill: 'white',
        },
        '&.size-26': {
          height: 26,
          width: 26,
        },
      },
      fontSizeLarge: { fontSize: '3.875rem' },
    },

    MuiAvatar: {
      root: {
        '&.border': {
          border: borderDefinition(undefined, '3px'),
        },
        '&.small': {
          width: spacer.spacer5,
          height: spacer.spacer5,
        },
        '&.large': {
          width: spacer.spacer7 + spacer.spacer3,
          height: spacer.spacer7 + spacer.spacer3,
        },
      },
    },

    ...navigationOverrides(palette, appBarMargin),
    ...containerOverrides,
    ...typographyOverrides,
    ...formOverrides(formControlSize),
    ...expansionPanelOverrides,
    ...cardOverrides,
    ...modalOverrides,
    ...buttonOverrides,
    ...tableOverrides,
    ...switchOverrides,
    ...accordionOverrides,
  },
});

const dealTheme = createTheme(dealThemePalette, bodyOverflowHidden, sizes.small, true);
const adminTheme = createTheme(dealThemePalette, bodyOverflowHidden, sizes.small, true);

const feelPrimaryMainNormalized = mainColor => {
  if (mainColor === null || mainColor === undefined || String(mainColor).trim() === '') {
    return null;
  }
  const raw = String(mainColor).trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    return `#${raw.toLowerCase()}`;
  }
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    const [r, g, b] = raw.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return String(mainColor).trim();
};

const feelTheme = mainColor => {
  if (mainColor) {
    const main = feelPrimaryMainNormalized(mainColor);
    return createTheme({ ...feelThemePalette, primary: { ...feelThemePalette.primary, main } }, bodyOverflowAuto, sizes.xsmedium, false);
  }
  return createTheme(feelThemePalette, bodyOverflowAuto, sizes.xsmedium, false);
};

export {
  adminTheme,
  borderDefinition,
  boxShadow,
  boxShadowLight,
  dealTheme,
  DEFAULT_SCALE,
  defaultPalette,
  feelTheme,
  feelThemePalette,
  largeBorderRadius,
  mediumBorderRadius,
  sizes,
  smallBorderRadius,
  spacer,
  typographyOverrides,
};
